import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Td,
  Th,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { usePutAccountMutation } from 'api';
import { AccountResponse } from 'api/bindings/AccountResponse';
import { PutAccountRequest } from 'api/bindings/PutAccountRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import { usePagination, useSlicePagination } from 'core';
import { AppButton, AppInput, FormButtonGroup } from 'features/components';
import { AppTable } from 'features/table';
import { useCallback, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FiPenTool, FiPlus } from 'react-icons/fi';
import { CreateSubAccountDrawer } from './CreateSubAccountDrawer';
import { EditSubAccountDrawer } from './EditSubAccountDrawer';

export interface EditAccountDrawerProps
  extends Omit<DrawerProps, 'children' | 'onSubmit'> {
  account: AccountResponse;
  refetch: () => void;
}

interface Form extends PutAccountRequest {
  id: string;
}

export const EditAccountDrawer = (props: EditAccountDrawerProps) => {
  const { account, refetch, ...rest } = props;

  // Fetches
  const [putAccount] = usePutAccountMutation();

  // State
  const toast = useToast();
  const [subAccountPagination, setSubAccountPagination] = useSlicePagination(
    account.subAccounts
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: account,
  });
  const [creatingSubAccountMode, setCreatingSubAccountMode] = useState<any>();
  const [editingSubAccount, setEditingSubAccount] =
    useState<SubAccountResponse>();
  const { watch, reset, control, handleSubmit } = form;
  const [accountCode, accountName] = watch(['code', 'name']);

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await putAccount(form);
        if ('error' in result) {
          toast({
            title: `Failed to update account ${form.code} - ${form.name}`,
            description: (result.error as any)?.data.message,
            status: 'error',
          });
          return;
        }
        toast({
          title: `Account ${form.code} - ${form.name} updated successfully!`,
          status: 'success',
        });
        reset();
        rest.onClose();
        refetch();
      } catch {}
    },
    [putAccount, refetch, reset, rest, toast]
  );

  const subAccountsBody = (
    <>
      {subAccountPagination.items.map((sa, i) => (
        <Tr key={i}>
          <Td>
            <IconButton
              aria-label="Edit Sub Account"
              icon={<FiPenTool />}
              onClick={() => setEditingSubAccount(sa)}
            />
          </Td>
          <Td>{sa.subAccountCode}</Td>
          <Td>{sa.subAccountName}</Td>
          <Td>{sa.subAccountDescription}</Td>
        </Tr>
      ))}
    </>
  );

  return (
    <Drawer {...rest}>
      {creatingSubAccountMode && (
        <CreateSubAccountDrawer
          isOpen={true}
          account={account}
          onClose={() => setCreatingSubAccountMode(undefined)}
          size="xl"
        />
      )}
      {editingSubAccount && (
        <EditSubAccountDrawer
          isOpen={true}
          account={account}
          subAccount={editingSubAccount}
          onClose={() => setEditingSubAccount(undefined)}
          size="xl"
        />
      )}
      <FormProvider {...form}>
        <DrawerOverlay />
        <DrawerCloseButton />
        <DrawerContent>
          <DrawerHeader>
            Editing Account: {accountCode} - {accountName}
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormControl>
                  <FormLabel htmlFor="code">Code:</FormLabel>
                  <Controller
                    control={control}
                    name="code"
                    rules={{
                      required: {
                        value: true,
                        message: `Account code is required.`,
                      },
                      min: {
                        value: 0,
                        message: `Account code must be zero or higher.`,
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: 'Account code must be numeral.',
                      },
                    }}
                    render={(f) => (
                      <AppInput type="number" id="code" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    control={control}
                    name="name"
                    rules={{
                      required: {
                        value: true,
                        message: `Account name is required.`,
                      },
                      minLength: {
                        value: 3,
                        message: `Account name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Account name must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="description">Description:</FormLabel>
                  <Controller
                    control={control}
                    name="description"
                    rules={{
                      maxLength: {
                        value: 256,
                        message: `Description must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="description" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </form>
              <AppTable
                title={<b>Sub Accounts</b>}
                buttonGroup={(props) => (
                  <AppButton
                    leftIcon={<FiPlus />}
                    variant={'solid'}
                    onClick={() => setCreatingSubAccountMode(true)}
                  >
                    New Sub Account
                  </AppButton>
                )}
                header={
                  <>
                    <Tr>
                      <Th></Th>
                      <Th>Name</Th>
                      <Th>Code</Th>
                      <Th>Description</Th>
                    </Tr>
                  </>
                }
                pagination={subAccountPagination}
                onPaginationChange={setSubAccountPagination}
                body={subAccountsBody}
              />
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onConfirm={handleSubmit(onSubmit)}
              onCancel={rest.onClose}
            />
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  );
};
